import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Clock,
  CheckCircle,
  Bell,
  Package,
  Loader2,
  AlertCircle,
  RefreshCw,
  Volume2,
  VolumeX
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const OrderTracking = () => {
  const { trackingToken } = useParams();
  const [order, setOrder] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [soundUnlocked, setSoundUnlocked] = useState(false);
  const [previousStatus, setPreviousStatus] = useState(null);
  const audioContextRef = useRef(null);

  // Initialize audio context on user interaction
  const unlockAudio = useCallback(() => {
    try {
      // Create and store audio context
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
      
      // Play a silent sound to unlock audio
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      gainNode.gain.value = 0.001; // Almost silent
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.1);
      
      setSoundUnlocked(true);
      
      // Play test sound
      setTimeout(() => playNotificationSound(), 200);
      
      // Vibrate to confirm
      if (navigator.vibrate) {
        navigator.vibrate(100);
      }
    } catch (e) {
      console.error('Could not unlock audio:', e);
    }
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      const ctx = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      
      const playTone = (frequency, startTime, duration) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.5, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      // Play a pleasant 3-note melody (louder)
      const now = ctx.currentTime;
      playTone(523.25, now, 0.3);        // C5
      playTone(659.25, now + 0.3, 0.3);  // E5
      playTone(783.99, now + 0.6, 0.5);  // G5
      
      // Repeat melody
      setTimeout(() => {
        try {
          const ctx2 = new (window.AudioContext || window.webkitAudioContext)();
          const now2 = ctx2.currentTime;
          const playTone2 = (freq, start, dur) => {
            const osc = ctx2.createOscillator();
            const gain = ctx2.createGain();
            osc.connect(gain);
            gain.connect(ctx2.destination);
            osc.frequency.value = freq;
            osc.type = 'sine';
            gain.gain.setValueAtTime(0.5, start);
            gain.gain.exponentialRampToValueAtTime(0.01, start + dur);
            osc.start(start);
            osc.stop(start + dur);
          };
          playTone2(523.25, now2, 0.3);
          playTone2(659.25, now2 + 0.3, 0.3);
          playTone2(783.99, now2 + 0.6, 0.5);
        } catch(e) {}
      }, 1000);
      
      // Vibrate pattern
      if (navigator.vibrate) {
        navigator.vibrate([300, 100, 300, 100, 500]);
      }
    } catch (e) {
      console.error('Could not play notification sound:', e);
    }
  }, [soundEnabled]);

  const fetchOrder = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/order/${trackingToken}`);
      if (res.ok) {
        const data = await res.json();
        
        // Play sound when order becomes ready
        if (soundUnlocked && previousStatus && previousStatus !== 'ready' && data.order.status === 'ready') {
          playNotificationSound();
        }
        
        setPreviousStatus(data.order.status);
        setOrder(data.order);
        setRestaurant(data.restaurant);
        setError(null);
      } else {
        setError('Commande non trouvée');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }, [trackingToken, previousStatus, soundUnlocked, playNotificationSound]);

  useEffect(() => {
    fetchOrder();
    // Auto-refresh every 5 seconds for faster notification
    const interval = setInterval(fetchOrder, 5000);
    return () => clearInterval(interval);
  }, [fetchOrder]);

  const getStatusInfo = (status, orderType) => {
    const isDineIn = orderType === 'dine_in';
    const info = {
      pending: {
        icon: <Clock className="h-12 w-12 text-yellow-500" />,
        title: 'Commande reçue',
        description: 'Le restaurant a bien reçu votre commande',
        color: 'bg-yellow-500',
        bgColor: 'bg-yellow-50',
        progress: 20
      },
      confirmed: {
        icon: <CheckCircle className="h-12 w-12 text-blue-500" />,
        title: 'Commande confirmée',
        description: 'Le restaurant a confirmé votre commande',
        color: 'bg-blue-500',
        bgColor: 'bg-blue-50',
        progress: 45
      },
      preparing: {
        icon: <Clock className="h-12 w-12 text-orange-500 animate-pulse" />,
        title: 'En préparation',
        description: 'Votre commande est en cours de préparation',
        color: 'bg-orange-500',
        bgColor: 'bg-orange-50',
        progress: 70
      },
      ready: {
        icon: <Bell className="h-12 w-12 text-green-500 animate-bounce" />,
        title: 'Commande prête !',
        description: isDineIn ? 'Votre commande arrive à votre table' : 'Vous pouvez venir récupérer votre commande',
        color: 'bg-green-500',
        bgColor: 'bg-green-50',
        progress: 100
      },
      served: {
        icon: <CheckCircle className="h-12 w-12 text-blue-500" />,
        title: isDineIn ? 'Servie' : 'Récupérée',
        description: 'Merci et bon appétit !',
        color: 'bg-blue-500',
        bgColor: 'bg-blue-50',
        progress: 100
      },
      picked_up: {
        icon: <CheckCircle className="h-12 w-12 text-blue-500" />,
        title: 'Récupérée',
        description: 'Merci et bon appétit !',
        color: 'bg-blue-500',
        bgColor: 'bg-blue-50',
        progress: 100
      },
      cancelled: {
        icon: <AlertCircle className="h-12 w-12 text-red-500" />,
        title: 'Annulée',
        description: 'Cette commande a été annulée',
        color: 'bg-red-500',
        bgColor: 'bg-red-50',
        progress: 0
      }
    };
    return info[status] || info.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Chargement de votre commande...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">{error}</h1>
          <p className="text-gray-600 mb-4">
            Vérifiez que vous avez le bon lien ou QR code
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Réessayer
          </Button>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status, order.order_type);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="order-tracking-page">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-lg mx-auto px-4 py-4 text-center">
          {restaurant?.logo_url ? (
            <img src={restaurant.logo_url} alt={restaurant.name} className="h-12 mx-auto mb-2" />
          ) : (
            <Package className="h-10 w-10 text-orange-500 mx-auto mb-2" />
          )}
          <h1 className="font-bold text-lg text-gray-900">{restaurant?.name}</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Order Number */}
        <Card className={`${statusInfo.bgColor} border-0`}>
          <CardContent className="pt-6 text-center">
            <Badge className="mb-2 text-lg px-4 py-1">{order.order_number}</Badge>
            {order.table_label && (
              <p className="text-sm text-gray-500 mb-2" data-testid="tracking-table-label">{order.table_label}</p>
            )}
            <div className="my-6">
              {statusInfo.icon}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{statusInfo.title}</h2>
            <p className="text-gray-600">{statusInfo.description}</p>
            
            {/* Progress Bar */}
            <div className="mt-6">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${statusInfo.color} transition-all duration-500`}
                  style={{ width: `${statusInfo.progress}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>Commande reçue</span>
                <span>En préparation</span>
                <span>Prête</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sound Alert Toggle */}
        {['pending', 'confirmed', 'preparing'].includes(order.status) && (
          <Card className={soundUnlocked ? "border-green-500 border-2" : "border-orange-500 border-2"}>
            <CardContent className="pt-4">
              {!soundUnlocked ? (
                <div className="text-center">
                  <p className="font-medium text-orange-700 mb-3">
                    🔔 Activez l'alerte sonore
                  </p>
                  <Button
                    className="w-full bg-orange-500 hover:bg-orange-600 text-lg py-6"
                    onClick={unlockAudio}
                  >
                    <Volume2 className="h-6 w-6 mr-2" />
                    ACTIVER LE SON
                  </Button>
                  <p className="text-xs text-gray-500 mt-3">
                    Cliquez pour recevoir une alerte quand c'est prêt
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                    <Volume2 className="h-5 w-5" />
                    <span className="font-bold">Son activé !</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Vous serez alerté quand la commande sera prête
                  </p>
                  <div className="mt-3 bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-green-700">
                      📱 Gardez cette page ouverte
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-gray-500"
                    onClick={playNotificationSound}
                  >
                    Tester le son
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Order Details */}
        <Card>
          <CardContent className="pt-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Détail de la commande
            </h3>
            <div className="space-y-2">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between py-2 border-b last:border-0">
                  <div>
                    <span className="font-medium">{item.quantity}x</span>
                    <span className="ml-2">{item.dish_name}</span>
                  </div>
                  <span className="text-gray-600">{(item.quantity * (item.unit_price ?? item.price)).toFixed(2)}€</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between pt-3 mt-3 border-t font-bold text-lg">
              <span>Total</span>
              <span className="text-orange-600">{order.total.toFixed(2)}€</span>
            </div>
          </CardContent>
        </Card>

        {/* Timestamps */}
        <Card>
          <CardContent className="pt-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Commande passée</span>
                <span>{new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              {order.ready_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Prête à</span>
                  <span className="text-green-600 font-medium">
                    {new Date(order.ready_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}
              {order.picked_up_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Récupérée à</span>
                  <span>{new Date(order.picked_up_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Refresh Button */}
        <Button 
          variant="outline" 
          className="w-full" 
          onClick={fetchOrder}
          data-testid="refresh-order-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser le statut
        </Button>
      </div>
    </div>
  );
};

export default OrderTracking;
